import React from "react";
import LetsWork from "../components/LetsWorkTogether";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrain, faBuildingColumns, faCloud, faMobileScreen, faPaintBrush } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from "lib/i18n";
import Seo from "components/Seo";
import { getServiceListSchema } from "lib/seo";

const getLocalizedValue = (value, language) => {
  if (value && typeof value === "object") {
    return value[language] || value.en || Object.values(value)[0];
  }
  return value;
};

const Services = () => {
  const { t, language } = useTranslation();
  const serviceContent = Array.isArray(t("services.items")) ? t("services.items") : [];
  const serviceIcons = [
    {
      id: "enterprise",
      image: faBuildingColumns,
    },
    {
      id: "uiux",
      image: faPaintBrush,
    },
    {
      id: "cloud",
      image: faCloud,
    },
    {
      id: "mobile",
      image: faMobileScreen,
    },
    {
      id: "appliedAi",
      image: faBrain,
    },
  ];
  const serviceExtras = {
    enterprise: {
      tagline: {
        en: "Built for scale, compliance, and long-term maintainability.",
        fr: "Conçu pour l'évolutivité, la conformité et la maintenabilité.",
      },
      highlights: {
        en: ["Systems integration", "Data workflows", "Operational dashboards"],
        fr: ["Intégration des systèmes", "Flux de données", "Tableaux de bord"],
      },
    },
    uiux: {
      tagline: {
        en: "Design systems that keep every touchpoint consistent.",
        fr: "Des systèmes de design pour une cohérence totale.",
      },
      highlights: {
        en: ["Research & discovery", "Prototyping", "Design systems"],
        fr: ["Recherche et découverte", "Prototypage", "Systèmes de design"],
      },
    },
    cloud: {
      tagline: {
        en: "Cloud foundations that stay stable under growth.",
        fr: "Des bases infonuagiques stables face à la croissance.",
      },
      highlights: {
        en: ["Migration strategy", "Security hardening", "Cost optimization"],
        fr: ["Stratégie de migration", "Sécurisation", "Optimisation des coûts"],
      },
    },
    mobile: {
      tagline: {
        en: "Mobile experiences that feel native across platforms.",
        fr: "Des expériences mobiles fluides sur toutes les plateformes.",
      },
      highlights: {
        en: ["iOS & Android", "Offline-ready", "Analytics-ready"],
        fr: ["iOS et Android", "Mode hors ligne", "Prêt pour l'analytique"],
      },
    },
    appliedAi: {
      tagline: {
        en: "Research the workflow before you automate the promise.",
        fr: "Étudier le flux avant d'automatiser la promesse.",
      },
      highlights: {
        en: ["Use-case discovery", "Pilot design", "Governance blueprint"],
        fr: ["Découverte des cas d'usage", "Conception de pilotes", "Plan de gouvernance"],
      },
    },
  };

  const services = serviceIcons.map((icon) => ({
    ...icon,
    ...serviceContent.find((item) => item.id === icon.id),
  }));
  const isFrench = language === "fr";
  const title = isFrench
    ? "Services de développement logiciel sur mesure"
    : "Custom Software Development Services";
  const description = isFrench
    ? "Applications d'entreprise, design UI/UX, solutions infonuagiques et applications mobiles. Services logiciels sur mesure par Quivio."
    : "Enterprise applications, UI/UX design, cloud solutions, and mobile app development. Tailored software services from Quivio.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path="/services"
        schema={[getServiceListSchema(services)]}
      />
      <div className="services section-title mt-5">
        <div className="container text-white">
          <div className="row align-items-center">
            <div className="col-md-7 mx-auto">
              <div className="services-title mb-5 mt-5">
                <h1 className="title-font title-font-size">{t("services.heading")}</h1>
                <p className="title-text mt-4 mb-4 title-font-2">
                  {t("services.subheading")}
                </p>
              </div>
            </div>
          </div>
          <div className="row align-items-center">
            {services.map((service) => {
              const extra = serviceExtras[service.id];
              const tagline = extra ? getLocalizedValue(extra.tagline, language) : "";
              const highlights = extra ? getLocalizedValue(extra.highlights, language) : [];
              return (
                <div className="col-12 col-lg-6" key={service.id}>
                  <div className="service-boxes-vertical service-card">
                    <div className="service-icon">
                      <FontAwesomeIcon icon={service.image} aria-hidden="true" />
                    </div>
                    <div className="service-info-title">
                      <h3 className="service-name title-font-2">
                        {service.title}
                      </h3>
                    </div>
                    <div className="service-info">
                      <p className="service-description title-font-2">
                        {service.description}
                      </p>
                    </div>
                    {tagline && (
                      <p className="service-tagline title-font-2">{tagline}</p>
                    )}
                    {Array.isArray(highlights) && highlights.length > 0 && (
                      <ul className="service-highlights title-font-2">
                        {highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                    <div className="card-header-row">
                      <span className="card-tag">{t("labels.service")}</span>
                      <span className="card-dot" aria-hidden="true"></span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <LetsWork />
    </>
  );
};

export default Services;
